import { EditorDataModel } from '../../../../../../../../../domain/ui-model/element-modeles/property/text/editor-data-model';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import { getReverseFontSize } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-level-map';
import { FONT_SCALE } from '@editor/src/page/index/components/editor/config';
import type { IParagraphStyle, ITextRunStyle, IWrapperStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import DataTransform from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model/data-transform';
import { TEXT_BASELINE_OFFSET, TEXT_FILL_LIST, FONT_EFFECT_KEY, FONT_FILL_KEY, FONT_BORDER_KEY } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { reduceCharacterData } from '@editor/src/page/index/components/editor/data/character';
import { updateIndentationOnCharacterSet, updateIndentationOnCharacterRemove } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/character-utils/character-indent';
import { combineTextrunStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { handleParaIndentChange } from '@utils/indent';
import { ITextFillInfo } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { IBorderInfo } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { IEffectModelValCollection } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { BulletProps } from '@src/slide/application/service/bullets-and-numbering-dialog-manager/core/interface';
import type { ColumnDataEmu } from '@src/slide/application/service/column-dialog-manager/core/interface';
import { ITDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import {
    AlignType,
    IndentType,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller/editor-operations/constant';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import type { EditorController } from '../index';
import { getFillStyle } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/graphic-adapter/generate-graphics-model';

export class EditorOperations {
    private editor: EditorController;
    private readonly editorModel: EditorDataModel;
    private operationMap;

    public constructor(editorModel: EditorDataModel, editCallBack: EditorController) {
        this.editorModel = editorModel;
        this.initOperationMap();
        this.editor = editCallBack;
    }

    public operations(key: string, value, option): { isHandled: boolean, needUpdate: boolean } {
        const fn = this.operationMap[key];
        let isHandled = false;
        let needUpdate = true;
        if (fn) {
            needUpdate = fn(value, option);
            needUpdate = needUpdate === undefined ? true : needUpdate;
            isHandled = true;
        }
        return { isHandled, needUpdate };
    }

    /**
     * 设置段落缩进
     * @param val
     */
    public setIndent(val: string): boolean {
        if (this.editor.props.disableIndentation) {
            return false;
        }
        if (val === IndentType.INDENT_INCREASE) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.INDENT,
                action: TDWActionType.INDENT,
            });
        } else {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.INDENT,
                action: TDWActionType.DEC_INDENT,
            });
        }
        this.setParaIndent({
            type: val,
        });
        return false;
    }

    public setCharacter(bulletProps: BulletProps) {
        this.setCharacterAndUpdate({
            ...bulletProps,
            range: this.editor.isEditing ? 'default' : 'all',
        });
        return false;
    }


    /**
     * 设置字体
     * @param val
     */
    protected setFontSize(val): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TEXT_FORMAT,
            action: TDWActionType.TEXT_SIZE,
        });

        // 从val里找出数字... 'font-size-xxpt' -> 'xx'
        const n = val.match(/-([\d.]*)pt/);
        if (!n) return;
        let size = Number(n[1]);

        const selectAll = this.editor.isSelectAllText();
        const { resizeLevel } = this.editorModel;
        if (!expect(resizeLevel).isNullOrUndefined() && !selectAll) {
            size = Number(getReverseFontSize(size, resizeLevel));
        }
        const selection = this.editor.operationRange;
        this.setFontSizeInner(size, selection);
    }

    /**
     * 字体自增
     * @param val
     */
    protected setFontSizeUp(val): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TEXT_FORMAT,
            action: TDWActionType.TEXT_SIZE,
        });
        this.setFontSizeChange(val);
    }

    /**
     * 字体自减
     * @param val
     */
    protected setFontSizeDown(val): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TEXT_FORMAT,
            action: TDWActionType.TEXT_SIZE,
        });
        this.setFontSizeChange(val);
    }

    /**
     * 设置文字颜色
     * @param val
     */
    protected setFontColor(tdColor: ITDColor): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TEXT_FORMAT,
            action: TDWActionType.TEXT_COLOR,
        });
        if (tdColor.srgb_color) {
            tdColor.srgb_color = tdColor.srgb_color.toUpperCase();
        }
        this.setFontFill({
            tdColor,
        });

        const removeStyle = {};
        TEXT_FILL_LIST.map(key => removeStyle[key] = null);
        // 删除文字效果mark
        this.editor?.removeStylesForSelection?.({ ...removeStyle, fillBackground: null }, this.editor.operationRange);
    }

    /**
     * 设置文字背景色
     * @param val
     */
    protected setFontBackground(val: ITDColor | null): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TEXT_FORMAT,
            action: TDWActionType.TEXT_HIGHLIGHT,
        });

        return this.editor.setStylesForSelection({
            backgroundColor: val?.srgb_color || null,
        }, this.editor.operationRange);
    }

    /**
     * 设置textBody垂直对齐方式
     * @param val
     */
    protected setFontVerticalAlign(val): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TEXT_SET,
            action: TDWActionType.TEXT_VERTICAL_ALIGN,
        });

        switch (val) {
            case AlignType.VERTICAL_TOP:
                return this.editor.setStylesForTextBody({
                    alignItems: 'flex-start',
                });
            case AlignType.VERTICAL_CENTER:
                return this.editor.setStylesForTextBody({
                    alignItems: 'center',
                });
            case AlignType.VERTICAL_BOTTOM:
                return this.editor.setStylesForTextBody({
                    alignItems: 'flex-end',
                });
            default:
        }
    }

    /**
     * 设置段落左右对齐方式
     * @param val
     */
    protected setFontHorizontalAlign(val): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TEXT_SET,
            action: TDWActionType.TEXT_HORIONTAL_ALIGN,
        });

        switch (val) {
            case AlignType.HORIZONTAL_LEFT:
                return this.editor.setStylesForPara({
                    textAlign: 'left',
                }, this.editor.operationRange);
            case AlignType.HORIZONTAL_CENTER:
                return this.editor.setStylesForPara({
                    textAlign: 'center',
                }, this.editor.operationRange);
            case AlignType.HORIZONTAL_RIGHT:
                return this.editor.setStylesForPara({
                    textAlign: 'right',
                }, this.editor.operationRange);
            case AlignType.HORIZONTAL_JUSTIFY:
                return this.editor.setStylesForPara({
                    textAlign: 'justify',
                }, this.editor.operationRange);
            case AlignType.HORIZONTAL_DISTRIBUTE:
                return this.editor.setStylesForPara({
                    textAlign: 'distribute',
                }, this.editor.operationRange);
            default:
        }
    }

    /**
     * 设置字体
     * @param val
     */
    protected setFontFamily(val): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TEXT_FORMAT,
            action: TDWActionType.TEXT_FONT,
        });

        // 这里没法直接改fonts与lang
        // 在这里修改以后在ui->sdk时再将数据设回去
        return this.editor.setStylesForSelection({
            fontFamily: val,
            fonts: {
                ea: val,
                latin: val,
            },
        }, this.editor.operationRange);
    }

    /**
     * 同时设置两种语言的字体
     * @param val
     */
    protected setFontFamilyWithLang(val): void {
        if (!val.ea && !val.latin) {
            return;
        }
        const style: any = {
            fonts: {},
        };
        if (val.ea) {
            style.fonts.ea = val.ea;
        }
        if (val.latin) {
            style.fonts.latin = val.latin;
        }
        return this.editor.setStylesForSelection(style, this.editor.operationRange);
    }

    /**
     * 设置文字粗体
     * @param val
     */
    protected setFontBold(val): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TEXT_FORMAT,
            action: TDWActionType.TEXT_BOLD,
        });

        if (val) {
            return this.editor.setStylesForSelection({
                fontWeight: 'bold',
            }, this.editor.operationRange);
        }
        return this.editor.setStylesForSelection({
            fontWeight: 'normal',
        }, this.editor.operationRange);
    }

    /**
     * 设置文字斜体
     * @param val
     */
    protected setFontItalic(val): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TEXT_FORMAT,
            action: TDWActionType.TEXT_ITALIC,
        });

        if (val) {
            return this.editor.setStylesForSelection({
                fontStyle: 'italic',
            }, this.editor.operationRange);
        }
        return this.editor.setStylesForSelection({
            fontStyle: 'normal',
        }, this.editor.operationRange);
    }

    /**
     * 设置文字效果
     * @param val
     */
    protected setEffect(val: { effect: IEffectModelValCollection }): void {
        return this.editor.setStylesForSelection(val, this.editor.operationRange);
    }

    /**
     * 设置文字填充
     * @param val
     */
    protected setFontFill(val: ITextFillInfo): void {
        let fillInfo: any = { ...val };
        if (fillInfo.tdColor) {
            // editor 中文字显示使用 color 字符串
            fillInfo.color = fillInfo.tdColor.srgb_color;
        }
        /**
         * paragraphList上的填充字段是每个填充的key散在run上的，渲染层需要的填充字段是整合到fill这个key下的
         * 这里set的时候直接把两种不同的格式都set上去，避免渲染层<->uiModel之间来回转换
         */
        fillInfo = { ...fillInfo, fill: getFillStyle({ fillBackground: { ...fillInfo } }) };
        const selectionLeafNodeAndRangeRefs = this.editor.getLeafNodeAndRangeRef(this.editor.operationRange);
        const isContainLinkNode = selectionLeafNodeAndRangeRefs.some(({ node: textNode }) => textNode['origin-href']);
        if (isContainLinkNode) {
            selectionLeafNodeAndRangeRefs.forEach(({ node: textNode, rangeRef }) => {
                const hrefInfo = textNode['origin-href'];
                if (hrefInfo) {
                    this.editor.setStylesForSelection({
                        // 给链接加个colorType的标签，渲染的时候用于判断链接颜色
                        href: {
                            ...hrefInfo,
                            colorType: 'tx',
                        },
                        ...fillInfo,
                    }, rangeRef);
                } else {
                    this.editor.setStylesForSelection(fillInfo, rangeRef);
                }
            });
        } else {
            this.editor.setStylesForSelection(fillInfo, this.editor.operationRange);
        }
    }

    /**
     * 设置文字边框
     * @param val
     */
    protected setFontBorder(val: { border: IBorderInfo }): void {
        return this.editor.setStylesForSelection(val, this.editor.operationRange);
    }

    /**
     * 设置文字下划线
     * @param val
     */
    protected setFontUnderline(val): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TEXT_FORMAT,
            action: TDWActionType.TEXT_UNDERLINE,
        });

        if (val) {
            return this.editor.setStylesForSelection({
                textDecoration: 'underline',
            }, this.editor.operationRange);
        }
        return this.editor.setStylesForSelection({
            textDecoration: 'none',
        }, this.editor.operationRange);
    }

    /**
     * 设置文字删除线
     * @param val
     */
    protected setDelete(val): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TEXT_FORMAT,
            action: TDWActionType.TEXT_DELETE,
        });

        if (val) {
            return this.editor.setStylesForSelection({
                del: 'sng',
            }, this.editor.operationRange);
        }
        return this.editor.setStylesForSelection({
            del: 'none',
        }, this.editor.operationRange);
    }

    protected setParagraphProps(val: IParagraphStyle): void {
        this.handleParaPropsFontScale(val);
        if (expect(val.paddingTop).isNumber()) {
            val.rawPaddingTop = val.paddingTop;
        }
        if (expect(val.paddingBottom).isNumber()) {
            val.rawPaddingBottom = val.paddingBottom;
        }
        this.editor.setStylesForPara(val, this.editor.operationRange);
    }

    protected setTextBaseLineOffset(value: number): void {
        this.editor.setStylesForSelection({
            [TEXT_BASELINE_OFFSET]: value,
        }, this.editor.operationRange);
    }

    protected setTextDirection(value: TextDirectionType): void {
        this.editor.setStylesForTextBody({
            textDirection: value,
        });
    }

    protected setColumnsData(columnData: ColumnDataEmu): void {
        const { columnNumber, columnSpace } = columnData;
        const obj: Partial<IWrapperStyle> = {};
        if (expect(columnNumber).isNumber()) {
            obj.columnNumber = columnNumber;
        }
        if (expect(columnSpace).isNumber()) {
            obj.columnSpace = columnSpace;
        }
        if (Object.keys(obj).length > 0) {
            this.editor.setStylesForTextBody(obj);
        }
    }

    private setFontSizeInner(fontSize, selection?) {
        const f = fontSize * FONT_SCALE;
        return this.editor.setStylesForSelection({
            rawFontSize: fontSize,
            displayFontSize: f,
            fontSize: f,
            // TODO 给raw加上一段
            fontSizeFrom: 'textrun',
        }, selection);
    }

    private setFontSizeChange(val) {
        let increase = val.size || 0;
        const min = val.min || 8;
        const max = val.max || 192;
        const selectAll = this.editor.isSelectAllText();
        const { resizeLevel } = this.editorModel;
        // 部分选中的时候increase值需要根据resizeLevel转换
        if (resizeLevel && !selectAll) {
            increase = getReverseFontSize(increase, resizeLevel);
        }
        return this.setFontSizeIncrease(increase, min, max);
    }

    private setFontSizeIncrease = (increaseSize, min, max) => {
        const selectionLeafNodeAndRangeRefs = this.editor.getLeafNodeAndRangeRef(this.editor.operationRange);
        // 判断是否是光标状态
        const isCaret = this.editor.isEditing && !this.editor.isExpanded();
        selectionLeafNodeAndRangeRefs.forEach((item) => {
            const { node, rangeRef } = item as any;
            // 如果是光标态，当前样式就用 getStyle 获取，否则就获取当前节点的样式
            const currentProps = isCaret ? this.editor.getStyle() : node;
            const { fontSize, rawFontSize, displayFontSize, fontSizeFrom } = currentProps;
            // eslint-disable-next-line camelcase
            const raw_fontSize = currentProps['raw-fontSize'];
            const styles = Object.create(null);
            if (!expect(fontSize).isNullOrUndefined()) {
                styles.fontSize = this.getSize(
                    fontSize + (increaseSize * FONT_SCALE),
                    min * FONT_SCALE,
                    max * FONT_SCALE,
                );
            }
            if (!expect(rawFontSize).isNullOrUndefined()) {
                styles.rawFontSize = this.getSize(rawFontSize + increaseSize, min, max);
            }
            if (!expect(displayFontSize).isNullOrUndefined()) {
                styles.displayFontSize = this.getSize(
                    displayFontSize + (increaseSize * FONT_SCALE),
                    min * FONT_SCALE,
                    max * FONT_SCALE,
                );
            }
            if (!expect(fontSizeFrom).isNullOrUndefined()) {
                styles.fontSizeFrom = 'textrun';
            }
            // eslint-disable-next-line camelcase
            if (!expect(raw_fontSize).isNullOrUndefined()) {
                styles['raw-fontSize'] = true;
            }
            // 全选时修改字体时displayFontSize需要改为fontSize
            if (this.editor.isSelectAllText()) {
                styles.displayFontSize = styles.fontSize;
            }
            this.editor.setStylesForSelection(styles, rangeRef);
        });
        if (selectionLeafNodeAndRangeRefs.length === 0) {
            this.handleFontSizeChangeNoSelection(increaseSize, min, max);
        }
    };

    private getSize = (size, min, max) => {
        if (size > max) return max;
        if (size < min) return min;
        return size;
    };

    /**
     * 未选中文本时，处理字体的变化
     */
    private handleFontSizeChangeNoSelection = (increaseSize, min, max) => {
        const style: ITextRunStyle = DataTransform.getOriginProps(this.editor.getStyle());
        if (style.displayFontSize) {
            const displayFontSize = this.getSize(
                style.displayFontSize + (increaseSize * FONT_SCALE),
                min * FONT_SCALE,
                max * FONT_SCALE,
            );
            const nextFontSize = displayFontSize / FONT_SCALE;
            this.setFontSizeInner(nextFontSize);
        }
    };

    private setCharacterAndUpdate = ({ type = '', color, size, customText, customImage, startNumber, range = 'default' } = {} as any, level?) => {
        const editorValue = this.editor.getEditorValue();
        const { paragraphList } = this.editor.getConvertFromDocElement(editorValue);
        const selectedParas = this.editor.getSelectionNodes(node => node.type === 'paragraph');
        const startParaPath = selectedParas?.[0]?.[1];
        const endParaPath = selectedParas?.[selectedParas?.length - 1]?.[1];
        const startParaIndex = startParaPath?.[startParaPath?.length - 1];
        const endParaIndex = endParaPath?.[endParaPath?.length - 1];

        const p = paragraphList.map((para, index) => {
            // 不是选择全部的情况下只对选中区处理
            if (range !== 'all' && (index < startParaIndex || index > endParaIndex)) return para;

            // 在没有手动指定的情况下，优先使用之前的level
            const l = this.getLevel(level, para);
            const paragraphCharacter: any = reduceCharacterData(
                para.paragraphCharacter,
                { type, color, size, customText, customImage, startNumber },
            );

            // raw里存储的是非继承值的key，提交sdk时需要根据raw里的key判断哪些是需要转换的
            paragraphCharacter.raw = Object.keys(paragraphCharacter);

            const nP = {
                ...para,
                paragraphCharacter,
            };

            const textrunStyle = combineTextrunStyle({
                textRunStyle: nP.textRuns?.[0]?.styles,
            });
            if (paragraphCharacter.character) {
                // 设置或修改项目符号
                nP.paragraphProps = updateIndentationOnCharacterSet({
                    level: l,
                    fontSize: textrunStyle.rawFontSize,
                    marginLeft: nP.paragraphProps.marginLeft,
                    textIndent: nP.paragraphProps.textIndent,
                    characterType: type,
                    shapeModel: this.editorModel.model,
                }, nP.paragraphProps);
            } else {
                // 删除项目符号
                nP.paragraphProps = updateIndentationOnCharacterRemove({
                    shapeModel: this.editorModel.model,
                    level: l,
                    marginLeft: nP.paragraphProps.marginLeft,
                    textIndent: nP.paragraphProps.textIndent,
                }, nP.paragraphProps);
            }

            if (!expect(level).isNullOrUndefined()) {
                nP.paragraphProps.level = level;
            }

            return nP;
        });

        this.editor.updateRawData(p, undefined, undefined, this.editorModel.autoFitType);
    };

    private getLevel(level, para) {
        return level || para.paragraphProps.level || 0;
    }

    private setParaIndent = ({ type = IndentType.INDENT_INCREASE } = {}) => {
        const editorValue = this.editor.getEditorValue();
        const { paragraphList } = this.editor.getConvertFromDocElement(editorValue);
        const selectedParas = this.editor.getSelectionNodes(node => node.type === 'paragraph');
        const startParaPath = selectedParas?.[0]?.[1];
        const endParaPath = selectedParas?.[selectedParas.length - 1]?.[1];
        const startParaIndex = startParaPath?.[startParaPath?.length - 1];
        const endParaIndex = endParaPath?.[endParaPath?.length - 1];
        const p = paragraphList.map((para, index) => {
            if ((index < startParaIndex || index > endParaIndex)) return para;
            return handleParaIndentChange(para, type, this.editorModel.model);
        });
        this.editor.updateRawData(p, undefined, undefined, this.editorModel.autoFitType);
    };

    private handleParaPropsFontScale(paraProps: IParagraphStyle): void {
        const needScaleProps: (keyof IParagraphStyle)[] = [
            'marginLeft',
            'textIndent',
            'paddingTop',
            'paddingBottom',
            'tabSize',
        ];
        if (paraProps.lineHeight && paraProps.lineHeightIs === 'absolute') {
            needScaleProps.push('lineHeight');
        }
        needScaleProps.forEach((prop) => {
            if (typeof paraProps[prop] === 'number') {
                paraProps[(prop as string)] = paraProps[prop] as number * FONT_SCALE;
            }
        });
        if (paraProps.tabList) {
            paraProps.tabList = paraProps.tabList.map(tabstop => ({
                ...tabstop,
                position: tabstop.position * FONT_SCALE,
            }));
        }
    }

    private initOperationMap(): void {
        this.operationMap = {
            'font-size': val => this.setFontSize(val),
            'font-size-up': val => this.setFontSizeUp(val),
            'font-size-down': val => this.setFontSizeDown(val),
            'font-color': (val: ITDColor) => this.setFontColor(val),
            'font-background': val => this.setFontBackground(val),
            'font-vertical-align': val => this.setFontVerticalAlign(val),
            'font-horizontal-align': val => this.setFontHorizontalAlign(val),
            'font-family': val => this.setFontFamily(val),
            'font-bold': val => this.setFontBold(val),
            'font-italic': val => this.setFontItalic(val),
            'font-underline': val => this.setFontUnderline(val),
            'font-delete': val => this.setDelete(val),
            [ActionType.BULLET_SET]: val => this.setCharacter(val),
            'paragraph-props': val => this.setParagraphProps(val),
            indent: val => this.setIndent(val),
            [FONT_EFFECT_KEY]: val => this.setEffect(val),
            'text-baseline-offset': val => this.setTextBaseLineOffset(val),
            [ActionType.TEXT_DIRECTION]: (val: TextDirectionType) => this.setTextDirection(val),
            [FONT_FILL_KEY]: val => this.setFontFill(val),
            [FONT_BORDER_KEY]: val => this.setFontBorder(val),
            [ActionType.UNIFORM_FONT]: val => this.setFontFamilyWithLang(val),
            [ActionType.COLUMNS]: (val: ColumnDataEmu) => this.setColumnsData(val),
        };
    }
}
